import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSalesOutcomeStatus } from "@/hooks/useSalesOutcomeStatus";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV_LINKS = [
  { label: "How It Works", hash: "how-it-works" },
  { label: "Free Scan", hash: "free-scan" },
  { label: "For Business", hash: "problem-framework" },
];

const navLinkClass = "font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { enabled: salesOutcomeEnabled } = useSalesOutcomeStatus(!!user);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const handleAnchorClick = (e, hash) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border print:hidden" data-testid="main-navbar">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
          <span className="h-2 w-2 bg-foreground transition-transform group-hover:scale-125" aria-hidden="true" />
          <span className="font-heading text-xl tracking-tight text-foreground">A8A</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link key={l.hash} to={`/#${l.hash}`} onClick={(e) => handleAnchorClick(e, l.hash)} className={navLinkClass} data-testid={`navbar-link-${l.hash}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {user === undefined ? null : user === null ? (
            <>
              <Link to="/login" className={navLinkClass} data-testid="navbar-login-link">Sign In</Link>
              <Button asChild size="sm" data-testid="navbar-cta-button">
                <Link to="/scan">Start Free &rarr;</Link>
              </Button>
            </>
          ) : (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className={navLinkClass} data-testid="navbar-admin-link">Admin</Link>
              )}
              {user.role === "admin" && salesOutcomeEnabled && (
                <Link to="/admin/sales-outcome" className={navLinkClass} data-testid="navbar-sales-outcome-link">Sales Outcome Engine</Link>
              )}
              {user.role !== "admin" && salesOutcomeEnabled && (
                <Link to="/sales-outcome" className={navLinkClass} data-testid="navbar-sales-outcome-customer-link">Sales Outcome Engine</Link>
              )}
              <Link to="/dashboard" className={navLinkClass} data-testid="navbar-dashboard-link">Dashboard</Link>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="navbar-logout-button">Log out</Button>
            </>
          )}
        </div>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <button className="p-2 -mr-2 text-foreground" data-testid="navbar-mobile-menu-button" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="rounded-none w-full sm:max-w-xs bg-background flex flex-col gap-1" data-testid="navbar-mobile-menu">
            <SheetTitle className="font-heading text-lg">Menu</SheetTitle>
            <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
            {NAV_LINKS.map((l) => (
              <Link
                key={l.hash}
                to={`/#${l.hash}`}
                onClick={(e) => handleAnchorClick(e, l.hash)}
                className="py-3 border-b border-border font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                data-testid={`navbar-mobile-link-${l.hash}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              {user === undefined ? null : user === null ? (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="navbar-mobile-login-link">
                    Sign In
                  </Link>
                  <Button asChild size="sm" onClick={() => setMenuOpen(false)} data-testid="navbar-mobile-cta-button">
                    <Link to="/scan">Start Free &rarr;</Link>
                  </Button>
                </>
              ) : (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="navbar-mobile-admin-link">
                      Admin
                    </Link>
                  )}
                  {user.role === "admin" && salesOutcomeEnabled && (
                    <Link to="/admin/sales-outcome" onClick={() => setMenuOpen(false)} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="navbar-mobile-sales-outcome-link">
                      Sales Outcome Engine
                    </Link>
                  )}
                  {user.role !== "admin" && salesOutcomeEnabled && (
                    <Link to="/sales-outcome" onClick={() => setMenuOpen(false)} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="navbar-mobile-sales-outcome-customer-link">
                      Sales Outcome Engine
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" data-testid="navbar-mobile-dashboard-link">
                    Dashboard
                  </Link>
                  <Button variant="outline" size="sm" onClick={handleLogout} data-testid="navbar-mobile-logout-button">Log out</Button>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
